import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faTrashAlt, faInfoCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import patientService from '../../services/patientService';
import ActionButtons from '../Common/ActionButtons/ActionButtons';
import { handleGlobalModalEscape } from '../../utils/modalEscape';
import {
  ALLOWED_DOCUMENT_MIMES,
  DOCUMENT_FILE_SIZE_ERROR,
  MAX_PATIENT_DOCUMENTS,
  buildDocumentFieldErrors,
  isFileAtOrOverDocumentLimit,
  extractDocumentsRowsFromResponse,
  getPendingDocumentUploads,
  normalizeSavedDocumentRow,
} from '../../utils/patientDocumentFormUtils';
import {
  formatPatientDocumentFileLabel,
  formatPatientDocumentName,
  openPatientDocumentView,
} from '../../utils/patientDocumentView';
import './PatientDocumentsSection.css';

const DOCUMENT_UPLOAD_RULES =
  'Each file must be 10 MB or smaller. Allowed types: PDF, Word, TXT, JPG, PNG, XML.';

const emptyDocumentRow = () => ({ fileName: '', file: null });

export const PatientDocumentsSection = forwardRef(function PatientDocumentsSection(
  {
    practiceId,
    patientId,
    isEditMode = false,
    isReadOnly = false,
    errors = {},
    setErrors,
    showSavedTable = true,
    title,
    showUploadRows = true,
    variant = 'default',
    autoUpload = false,
    showUpdateButton = false,
    updateButtonLabel = 'Update',
    onPendingDocumentsChange,
  },
  ref
) {
  const [documents, setDocuments] = useState([emptyDocumentRow()]);
  const [savedDocuments, setSavedDocuments] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(
    () => Boolean(practiceId && patientId && showSavedTable)
  );
  const [isUploading, setIsUploading] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const fileInputRefs = useRef({});
  const renameInputRef = useRef(null);

  const loadSavedDocuments = useCallback(async () => {
    if (!practiceId || !patientId) return;
    setLoadingSaved(true);
    try {
      const res = await patientService.getPatientDocuments(practiceId, patientId);
      const rows = extractDocumentsRowsFromResponse(res).map(normalizeSavedDocumentRow);
      setSavedDocuments(rows);
    } catch (err) {
      console.error('Failed to load patient documents:', err);
      setSavedDocuments([]);
    } finally {
      setLoadingSaved(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (typeof onPendingDocumentsChange !== 'function') return undefined;
    onPendingDocumentsChange(getPendingDocumentUploads(documents).length);
    return undefined;
  }, [documents, onPendingDocumentsChange]);

  useEffect(() => {
    if (patientId && showSavedTable) {
      loadSavedDocuments();
    } else {
      setSavedDocuments([]);
    }
  }, [patientId, showSavedTable, loadSavedDocuments]);

  const uploadPendingDocuments = useCallback(async (patientRowId) => {
    const pending = getPendingDocumentUploads(documents);
    if (!patientRowId || pending.length === 0) {
      return { uploaded: 0, failed: 0 };
    }

    let uploaded = 0;
    let failed = 0;

    for (const { file, displayName, rowIndex } of pending) {
      try {
        await patientService.uploadPatientDocument(
          practiceId,
          patientRowId,
          file,
          displayName
        );
        uploaded += 1;
        if (setErrors) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next[`documentFile_${rowIndex}`];
            return next;
          });
        }
      } catch (docErr) {
        failed += 1;
        const msg =
          docErr?.response?.data?.message ||
          DOCUMENT_FILE_SIZE_ERROR;
        if (setErrors) {
          setErrors((prev) => ({ ...prev, [`documentFile_${rowIndex}`]: msg }));
        }
        console.error('Document upload failed:', docErr);
      }
    }

    if (uploaded > 0) {
      setDocuments([emptyDocumentRow()]);
      await loadSavedDocuments();
    }

    return { uploaded, failed };
  }, [documents, practiceId, setErrors, loadSavedDocuments]);

  useImperativeHandle(ref, () => ({
    validateDocuments: () => buildDocumentFieldErrors(documents),
    getPendingUploads: () => getPendingDocumentUploads(documents),
    uploadPendingDocuments,
    reloadSavedDocuments: loadSavedDocuments,
    resetPendingDocuments: () => setDocuments([emptyDocumentRow()]),
  }), [documents, uploadPendingDocuments, loadSavedDocuments]);

  const removeDocument = (index) => {
    setDocuments((prev) => {
      let next = prev.filter((_, i) => i !== index);
      if (next.length === 0) return [emptyDocumentRow()];
      const last = next[next.length - 1];
      if (last.file instanceof File) {
        next = [...next, emptyDocumentRow()];
      }
      return next;
    });
    if (setErrors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`documentFile_${index}`];
        delete next[`documentFileName_${index}`];
        return next;
      });
    }
  };

  const updateDocument = (index, field, value) => {
    setDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, [field]: value } : doc))
    );
  };

  const handleFileUpload = async (e, index) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const filesToAutoUpload = [];
    let nextDocumentRows = null;

    flushSync(() => {
      setDocuments((prev) => {
        const next = [...prev];
        const existingWithFiles = next.filter((d) => d.file instanceof File).length;
        let remainingSlots = Math.max(0, MAX_PATIENT_DOCUMENTS - existingWithFiles);
        let cursor = index;

        for (const file of files) {
          if (!ALLOWED_DOCUMENT_MIMES.includes(file.type)) {
            toast.error(`Invalid document type: ${file.name}. Allowed: PDF, Word, TXT, JPG, PNG, XML.`);
            continue;
          }

          if (isFileAtOrOverDocumentLimit(file)) {
            toast.error(DOCUMENT_FILE_SIZE_ERROR);
            while (cursor < next.length && next[cursor]?.file instanceof File) {
              cursor += 1;
            }
            const errIdx = cursor < next.length ? cursor : next.length;
            const prevRow = errIdx < next.length ? next[errIdx] : {};
            const slot = {
              ...prevRow,
              file: null,
              fileTooLargeName: file.name,
              fileName: (prevRow.fileName || '').trim() ? prevRow.fileName : '',
            };
            if (errIdx < next.length) next[errIdx] = slot;
            else next.push(slot);
            continue;
          }

          if (remainingSlots <= 0) {
            toast.warning(`Maximum ${MAX_PATIENT_DOCUMENTS} documents are allowed per patient.`);
            break;
          }

          while (cursor < next.length && next[cursor]?.file instanceof File) {
            cursor += 1;
          }

          const defaultName = String(file.name || '')
            .replace(/\.[^/.]+$/, '')
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .trim();

          const row = {
            fileName: defaultName || 'Document',
            file,
            fileTooLargeName: undefined,
          };

          const targetIndex = cursor < next.length ? cursor : next.length;
          if (cursor < next.length) {
            const { fileTooLargeName: _omit, ...rest } = next[cursor] || {};
            next[cursor] = { ...rest, fileName: row.fileName, file: row.file };
          } else {
            next.push(row);
          }

          if (autoUpload && patientId) {
            filesToAutoUpload.push({
              file,
              displayName: row.fileName,
              rowIndex: targetIndex,
            });
          }

          cursor += 1;
          remainingSlots -= 1;
        }

        if (next.length === 0 || next[next.length - 1].file instanceof File) {
          next.push(emptyDocumentRow());
        }

        nextDocumentRows = next;
        return next;
      });
    });

    e.target.value = '';

    if (setErrors && nextDocumentRows) {
      setErrors((prev) => {
        const nextErr = { ...prev };
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('documentFile')) delete nextErr[key];
        });
        return { ...nextErr, ...buildDocumentFieldErrors(nextDocumentRows) };
      });
    }

    if (autoUpload && patientId && filesToAutoUpload.length > 0) {
      let uploadedCount = 0;
      for (const { file, displayName, rowIndex } of filesToAutoUpload) {
        try {
          await patientService.uploadPatientDocument(practiceId, patientId, file, displayName);
          uploadedCount += 1;
          if (setErrors) {
            setErrors((prev) => {
              const next = { ...prev };
              delete next[`documentFile_${rowIndex}`];
              delete next[`documentFileName_${rowIndex}`];
              return next;
            });
          }
        } catch (docErr) {
          const msg =
            docErr?.response?.data?.message ||
            DOCUMENT_FILE_SIZE_ERROR;
          if (setErrors) {
            setErrors((prev) => ({ ...prev, [`documentFile_${rowIndex}`]: msg }));
          }
          toast.error(msg);
        }
      }

      if (uploadedCount > 0) {
        setDocuments([emptyDocumentRow()]);
        await loadSavedDocuments();
        toast.success(
          uploadedCount === 1
            ? 'Document uploaded successfully'
            : `${uploadedCount} documents uploaded successfully`
        );
      }
    }
  };

  const handleViewDocument = (doc) => {
    openPatientDocumentView(practiceId, patientId, doc);
  };

  const closeRenameModal = useCallback(() => {
    if (isRenaming) return;
    setRenameDoc(null);
    setRenameValue('');
    setRenameError('');
  }, [isRenaming]);

  const handleRenameDocument = (doc) => {
    if (isReadOnly) return;
    if (!practiceId || !patientId || doc?.id == null) return;
    setRenameDoc(doc);
    setRenameValue(String(doc.document_name || doc.name || '').trim());
    setRenameError('');
  };

  const submitRenameDocument = async () => {
    if (!renameDoc || !practiceId || !patientId) return;
    const name = String(renameValue).trim();
    if (!name) {
      setRenameError('Name cannot be empty');
      return;
    }

    setIsRenaming(true);
    try {
      const res = await patientService.patchPatientDocument(practiceId, patientId, renameDoc.id, { name });
      if (res.data?.success) {
        toast.success('Document name updated');
        setSavedDocuments((prev) =>
          prev.map((row) => (row.id === renameDoc.id ? { ...row, name, document_name: name } : row))
        );
        setRenameDoc(null);
        setRenameValue('');
        setRenameError('');
      } else {
        setRenameError(res.data?.message || 'Update failed');
      }
    } catch (err) {
      setRenameError(err.response?.data?.message || 'Update failed');
    } finally {
      setIsRenaming(false);
    }
  };

  useEffect(() => {
    if (!renameDoc) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);

    const onKeyDown = (event) => {
      if (isRenaming) return;
      handleGlobalModalEscape(event, closeRenameModal);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [renameDoc, isRenaming, closeRenameModal]);

  const handleDeleteDocument = async (doc) => {
    if (isReadOnly) return;
    if (!practiceId || !patientId || doc?.id == null) return;
    const confirmed = window.confirm(`Delete document "${formatPatientDocumentName(doc)}"?`);
    if (!confirmed) return;

    const previousList = savedDocuments;
    setSavedDocuments((prev) => prev.filter((row) => row.id !== doc.id));
    try {
      const res = await patientService.deletePatientDocument(practiceId, patientId, doc.id);
      if (res.data?.success) {
        toast.success('Document removed');
      } else {
        setSavedDocuments(previousList);
        toast.error(res.data?.message || 'Delete failed');
      }
    } catch (err) {
      setSavedDocuments(previousList);
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const sectionTitle = title || (isEditMode ? 'Uploaded documents' : 'Upload documents');

  const pendingUploadCount = getPendingDocumentUploads(documents).length;

  const handleUpdateDocuments = async () => {
    if (!patientId) {
      toast.error('Save the patient first before uploading documents.');
      return;
    }
    const fieldErrors = buildDocumentFieldErrors(documents);
    if (Object.keys(fieldErrors).length > 0) {
      if (setErrors) setErrors((prev) => ({ ...prev, ...fieldErrors }));
      toast.error('Please fix document errors before updating.');
      return;
    }
    if (pendingUploadCount === 0) {
      toast.info('Select a file to upload first.');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadPendingDocuments(patientId);
      if (result.failed > 0) {
        toast.error(
          result.uploaded > 0
            ? `${result.uploaded} document(s) uploaded, but ${result.failed} failed.`
            : 'Document upload failed. Please try again.'
        );
        return;
      }
      toast.success(
        result.uploaded === 1
          ? 'Document uploaded successfully'
          : `${result.uploaded} documents uploaded successfully`
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
    <div className="patient-documents-section" id="patient-documents-section">
      <h3 className="patient-documents-section-title patient-documents-title-row">
        {sectionTitle}
        {!isReadOnly ? (
          <span
            className="patient-documents-info"
            tabIndex={0}
            role="img"
            aria-label={DOCUMENT_UPLOAD_RULES}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            <span className="patient-documents-info-tip" role="tooltip">{DOCUMENT_UPLOAD_RULES}</span>
          </span>
        ) : null}
      </h3>

      {showSavedTable && patientId && (
        <div className="patient-documents-table-wrap">
            <table className="patient-documents-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>File</th>
                  <th>Uploaded</th>
                  <th className="patient-documents-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingSaved ? (
                  <tr>
                    <td colSpan="5" className="patient-documents-empty">
                      <FontAwesomeIcon icon={faSpinner} spin /> Loading documents...
                    </td>
                  </tr>
                ) : savedDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="patient-documents-empty">
                      No documents uploaded yet.
                    </td>
                  </tr>
                ) : (
                  savedDocuments.map((doc, idx) => (
                    <tr key={doc.id != null ? `saved-doc-${doc.id}` : `saved-doc-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{formatPatientDocumentName(doc)}</td>
                      <td className="text-break">{formatPatientDocumentFileLabel(doc)}</td>
                      <td>
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="patient-documents-col-actions">
                        <ActionButtons
                          onView={() => handleViewDocument(doc)}
                          onEdit={isReadOnly ? undefined : () => handleRenameDocument(doc)}
                          onDelete={isReadOnly ? undefined : () => handleDeleteDocument(doc)}
                          hideEdit={isReadOnly}
                          hideDelete={isReadOnly}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
      )}

      {showUploadRows && (
        <div>
          {documents.map((doc, index) => {
            const hasPendingContent = doc.file instanceof File || Boolean(doc.fileTooLargeName);
            const showRemove = hasPendingContent || index < documents.length - 1;

            return (
            <div key={`doc-row-${index}`} className="patient-documents-row">
              <div className="patient-documents-col">
                <span className="patient-documents-field-label">File Name</span>
                <input
                  type="text"
                  className={`patient-documents-input ${errors[`documentFileName_${index}`] ? 'is-invalid' : ''}`}
                  value={doc.fileName}
                  onChange={(e) => updateDocument(index, 'fileName', e.target.value)}
                  placeholder="Enter file name"
                />
                {errors[`documentFileName_${index}`] && (
                  <div className="patient-documents-error">{errors[`documentFileName_${index}`]}</div>
                )}
              </div>
              <div className="patient-documents-col">
                <span className="patient-documents-field-label">Upload File</span>
                <div className="patient-documents-upload-group">
                  <input
                    type="text"
                    className={`patient-documents-file-display ${errors[`documentFile_${index}`] || doc.fileTooLargeName ? 'is-invalid' : ''}`}
                    readOnly
                    placeholder="No file selected"
                    value={doc.file ? doc.file.name : (doc.fileTooLargeName || '')}
                  />
                  <button
                    type="button"
                    className="patient-documents-upload-btn"
                    onClick={() => fileInputRefs.current[index]?.click()}
                  >
                    <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload
                  </button>
                  <input
                    ref={(el) => { fileInputRefs.current[index] = el; }}
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => handleFileUpload(e, index)}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xml"
                  />
                  {showRemove && (
                    <button
                      type="button"
                      className="patient-documents-icon-btn remove"
                      onClick={() => removeDocument(index)}
                      title="Remove row"
                      aria-label="Remove document row"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} className="patient-documents-remove-icon" />
                    </button>
                  )}
                </div>
                {(doc.fileTooLargeName || errors[`documentFile_${index}`]) && (
                  <div className="patient-documents-error">
                    {errors[`documentFile_${index}`] || DOCUMENT_FILE_SIZE_ERROR}
                  </div>
                )}
              </div>
            </div>
          );
          })}
          {showUpdateButton && patientId && (
            <div className="patient-documents-actions">
              <button
                type="button"
                className="patient-documents-update-btn"
                onClick={handleUpdateDocuments}
                disabled={isUploading || pendingUploadCount === 0}
              >
                {isUploading ? 'Uploading...' : updateButtonLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    {renameDoc
      ? createPortal(
          <div
            className="patient-documents-rename-overlay"
            role="presentation"
            onClick={() => {
              if (!isRenaming) closeRenameModal();
            }}
          >
            <div
              className="patient-documents-rename-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="patient-documents-rename-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="patient-documents-rename-header">
                <h5 id="patient-documents-rename-title" className="patient-documents-rename-title">
                  Edit document name
                </h5>
                <button
                  type="button"
                  className="patient-documents-rename-close"
                  onClick={closeRenameModal}
                  disabled={isRenaming}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form
                className="patient-documents-rename-body"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitRenameDocument();
                }}
              >
                <label className="patient-documents-field-label" htmlFor="patient-document-rename-input">
                  File name
                </label>
                <input
                  id="patient-document-rename-input"
                  ref={renameInputRef}
                  type="text"
                  className={`patient-documents-input ${renameError ? 'is-invalid' : ''}`}
                  value={renameValue}
                  onChange={(event) => {
                    setRenameValue(event.target.value);
                    if (renameError) setRenameError('');
                  }}
                  placeholder="Enter file name"
                  disabled={isRenaming}
                  maxLength={255}
                />
                {renameError ? (
                  <div className="patient-documents-error">{renameError}</div>
                ) : null}
                <div className="patient-documents-rename-footer">
                  <button
                    type="button"
                    className="patient-documents-rename-cancel"
                    onClick={closeRenameModal}
                    disabled={isRenaming}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="patient-documents-rename-save"
                    disabled={isRenaming}
                  >
                    {isRenaming ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      : null}
    </>
  );
});


export default PatientDocumentsSection;
